
import { reco_type_choices } from "../../interfaces";
import { getNestVal, getProp, not_empty_prop } from "../../util_glob";
import { calcDiscount } from "../../inv_lsts";
import {
    IStyle,
    IButtonStyles,
    ITooltipStyles,
    ICheckboxStyles,
    IDetailsRowProps,
    ITextFieldStyles,
    IDetailsRowStyles,
    IDetailsListStyles,
    IDetailsHeaderStyles
} from "@fluentui/react";
import { IColumn } from "@fluentui/react/lib/DetailsList";


export const shortRecoStatus = (gridData: any, recoType: reco_type_choices): string => {
    let discrpCount  = 0;
    let orphanInvCount = 0;
    let orphanOrdCount = 0;
    if (recoType == 'verify') {
        discrpCount = getNestVal(gridData, "purchase_order_info.count_of_items_with_discrepancies", 0);
        orphanInvCount = getNestVal(gridData, "purchase_order_info.count_of_orphan_invoice_items", 0);
        orphanOrdCount = getNestVal(gridData, "purchase_order_info.count_of_orphan_order_items", 0);
        return ``;
    } else if (recoType == 'reconcile') {
        return ``;
    }
    return ``;
}


// parses the backend's TotalPriceFlags __str__ (eg. "TotalPriceFlags(disc_applied=Y, vat_incl=N, excise_incl=N)")
// into a plain object of booleans/strings. Shared so InvoiceItemsTable's live recalculation and the
// mismatch check below always read the same flags the same way.
export const parseFlags = (str: string) => {
    const inside = str?.match(/\((.*)\)/)?.[1];
    if (!inside) return {};

    return Object.fromEntries(
      inside.split(',').map(pair => {
        const [key, val] = pair.split('=').map(s => s.trim());

        let parsedVal;
        if (val === 'Y') parsedVal = true;
        else if (val === 'N') parsedVal = false;
        else parsedVal = val;

        return [key, parsedVal];
      })
    );
}

// Invoice-level excise gap needed to reconcile Σ(item totals) against invoice_total_net/gross.
// total_price is usually already each item's true net merchandise value (excise excluded — see AI
// extraction instructions for total_price), but invoice_total_net/gross are VAT taxable bases that
// DO include excise (excise is levied before VAT). Backend's winning Phase 1 case already decided,
// invoice-wide, whether excise needed adding at the sum level (kgnf_validator_init
// ._compute_items_sum_for_flags) — every item in one invoice shares the same winning `_p1_flags`,
// so reading it off the first item is sufficient. Single source of truth for this adjustment so it
// can't drift between the different mismatch checks that need it.
export const computeInvoiceExciseSum = (items: any[]): number => {
    if (!Array.isArray(items) || items.length === 0) return 0;
    const flags = parseFlags(getProp(items[0], '_p1_flags', ''));
    if (flags?.excise_incl === true) return 0;
    return items.reduce((sum: number, it: any) => sum + (Math.abs(Number(getProp(it, 'excise_tax', 0))) || 0), 0);
}

// mirrors backend's kgnf_validator_init._compute_effective_total + the
// post_subtract_item_vat correction from apply_phase1_case, applied on top of the item's raw
// `total_price` (as opposed to InvoiceItemsTable's live recalculation, which rebuilds a fresh
// total from unit_price × qty while the user is editing those specific fields).
export const computeEffectiveTotalPrice = (item: any): number => {
    // Backend already baked the reconciled NET onto total_price (VAT removed,
    // discount/excise applied) — see kgnf_inv_valid_v3b.align_line_prices_v3b.
    // Replaying the flags here would double-transform, so return total_price as-is.
    if (getProp(item, 'line_price_aligned', false) === true) {
        return Number(getProp(item, 'total_price', 0)) || 0;
    }

    const flags = parseFlags(getProp(item, '_p1_flags', ''));

    const qty      = Number(getProp(item, 'quantity', 0)) || 0;
    const unit     = Number(getProp(item, 'unit_price', 0)) || 0;
    const discount = Math.abs(Number(getProp(item, 'discount', 0)) || 0);

    let effective = Number(getProp(item, 'total_price', 0)) || 0;

    // discount_ignored: backend rebuilds from qty × unit_price when that product is usable
    if (flags?.disc_ignored === true) {
        const calc = qty * unit;
        if (calc > 0) effective = calc;
    }

    const needsDiscount = discount > 0 && flags?.disc_applied !== true && flags?.disc_ignored !== true;
    const needsVatRemoval = flags?.vat_incl === true;

    const applyDiscount = (val: number): number => {
        if (!needsDiscount) return val;
        if (flags?.disc_is_pct === true) return val - calcDiscount(val, discount, true);
        return val - discount;
    };
    const removeVat = (val: number): number => val - (Number(getProp(item, 'item_vat', 0)) || 0);

    if (needsDiscount && needsVatRemoval && flags?.vat_before_disc === true) {
        effective = removeVat(effective);
        effective = applyDiscount(effective);
    } else {
        effective = applyDiscount(effective);
        if (needsVatRemoval) effective = removeVat(effective);
    }

    if (getProp(item, 'post_subtract_item_vat', false) === true) {
        effective -= (Number(getProp(item, 'item_vat', 0)) || 0);
    }

    return effective;
}

// calculated_total_price is backend's source of truth — never adjusted to force agreement with
// total_price. A raw difference is a real signal (bad flags, bad extraction, genuinely broken
// reconciliation) and should stay visible, not be silently reconciled away.
export const isTotToTotMssmatched = (prps: IDetailsRowProps | undefined): boolean => {
    if (!prps?.item) return false;
    return Math.abs((prps.item?.['calculated_total_price'] ?? 0) - (prps.item?.['total_price'] ?? 0)) >= 0.2;
}

// When total_price and calculated_total_price genuinely differ, try to explain WHY using only
// known, named transformations (vat removal, discount reapplication, the post_subtract_item_vat
// invoice-level correction) — replaying `_p1_flags` via computeEffectiveTotalPrice. If that
// reconstruction lands back on calculated_total_price, the difference has a legitimate cause and
// is safe to surface as informational rather than a warning. If it DOESN'T land on
// calculated_total_price either, there's no known explanation — that's a real, unexplained
// mismatch and must stay flagged. This never changes calculated_total_price itself.
export const explainTotalPriceMismatch = (item: any): string | null => {
    if (!item) return null;

    const rawTotal   = Number(getProp(item, 'total_price', 0)) || 0;
    const calcTotal  = Number(getProp(item, 'calculated_total_price', 0)) || 0;
    if (Math.abs(calcTotal - rawTotal) < 0.2) return null; // no mismatch to explain

    const reconstructed = computeEffectiveTotalPrice(item);
    if (Math.abs(reconstructed - calcTotal) >= 0.2) return null; // known transforms don't explain it either

    const flags   = parseFlags(getProp(item, '_p1_flags', ''));
    const reasons: string[] = [];

    if (flags?.vat_incl === true) reasons.push(`VAT removed (${getProp(item, 'item_vat', 0)})`);
    if (getProp(item, 'post_subtract_item_vat', false) === true) reasons.push(`VAT removed via invoice-level match (${getProp(item, 'item_vat', 0)})`);
    if (flags?.disc_applied !== true && flags?.disc_ignored !== true && Math.abs(Number(getProp(item, 'discount', 0)) || 0) > 0) {
        reasons.push(`Discount reapplied (${getProp(item, 'discount', 0)}${flags?.disc_is_pct === true ? '%' : ''})`);
    }
    if (flags?.disc_ignored === true) reasons.push('Discount column ignored — total rebuilt from qty × unit price');

    return reasons.length > 0 ? reasons.join('; ') : 'Explained by known total_price presentation flags';
}

export const invDetailsRowMainStyles: Partial<IDetailsRowStyles> = {
    root: {
        fontSize: "10px",
        fontFamily: "Urbanist",
        selectors: {
            ":hover": {
                backgroundColor: "#f3f2f1"
            }
        },
        minHeight: "20px !important",
        padding: "0px !important",
        margin: "0px"
    },
    cell: {
        padding: "2px",
        margin: "0px",
        minHeight: "20px !important",
        height: "36px !important",
        fontSize: "10px",
        fontFamily: "Urbanist, Roboto"
    },
    fields: {
        padding: "0px !important",
        margin: "0px !important",
        fontSize: "10px",
        fontFamily: "Urbanist"
    }
};

export const getQuantityToUse = (item: any) => {
    //** if is_compound_unit true › compound_quantity else › quantity */
    let q = getProp(item, 'quantity', 0);
    if ((getProp(item, 'is_compound_unit', false)) == true) q = getProp(item, 'compound_quantity', q);
    return q;
}

// Stable key identifying the PO LINE ITEM a given invoice item is linked to. Prefer the unique DB row
// id (reconciliation.id); fall back to PO document id + PO line number so items still group correctly
// when id is absent. Returns "" when the item is not linked to any PO line.
export const getLinkedPoLineKey = (item: any): string => {
    const rowId = String(getNestVal(item, 'reconciliation.id', '') ?? '').trim();
    if (rowId) return `rid:${rowId}`;
    const poId   = String(getNestVal(item, 'reconciliation.ai_recon.purchase_order_id', '') ?? getNestVal(item, 'purchase_order.order_id_value', '') ?? '').trim();
    const poLine = String(getNestVal(item, 'reconciliation.ai_recon.purchase_order_item_number', '') ?? '').trim();
    if (poId && poLine) return `po:${poId}#${poLine}`;
    return '';
}

// true when at least one invoice item is linked to a PO line (drives button visibility)
export const hasLinkedPurchaseOrders = (items: any[]): boolean => {
    if (!Array.isArray(items)) return false;
    return items.some(it => getLinkedPoLineKey(it) !== '');
}

const toNum = (v: any): number => {
    if (v === null || v === undefined || v === '') return 0;
    const n = typeof v === 'number' ? v : parseFloat(String(v).replace(/,/g, '').trim());
    return isNaN(n) ? 0 : n;
}

const round2 = (n: number): number => Math.round(n * 100) / 100;

// Merge invoice items that are linked to the SAME PO line item into one row. Only items that have a
// numeric total_price value participate in a merge; everything else is left untouched. For each merge
// group the totals are summed following the existing sum rules (plain numeric sum, money rounded to
// 2dp) and the merged row's description is set to the linked PO item's description
// (reconciliation.item_description). Returns a NEW array and a count of how many rows were collapsed.
export const mergeItemsByLinkedPo = (items: any[]): { merged: any[]; mergedGroups: number; removed: number } => {
    if (!Array.isArray(items) || items.length === 0) return { merged: items || [], mergedGroups: 0, removed: 0 };

    const groups = new Map<string, any[]>();   // po line key -> mergeable items
    const passthrough: { idx: number; item: any }[] = [];

    items.forEach((it, idx) => {
        const key = getLinkedPoLineKey(it);
        const hasTotal = not_empty_prop(it, 'total_price') && !isNaN(toNum(it.total_price));
        if (key && hasTotal) {
            if (!groups.has(key)) groups.set(key, []);
            groups.get(key)!.push({ ...it, __srcIdx: idx });
        } else {
            passthrough.push({ idx, item: it });
        }
    });

    const result: { idx: number; item: any }[] = [...passthrough];
    let mergedGroups = 0;
    let removed = 0;

    groups.forEach((groupItems) => {
        if (groupItems.length <= 1) {
            // nothing to merge — keep the single item at its original position
            const only = groupItems[0];
            result.push({ idx: only.__srcIdx, item: (() => { const c = { ...only }; delete c.__srcIdx; return c; })() });
            return;
        }

        mergedGroups += 1;
        removed += groupItems.length - 1;

        // base the merged row on the first item in the group (preserves ordering + flags)
        const base: any = { ...groupItems[0] };
        delete base.__srcIdx;

        base.total_price            = round2(groupItems.reduce((acc, it) => acc + toNum(it.total_price), 0)) ?? 0.00;
        base.calculated_total_price = round2(groupItems.reduce((acc, it) => acc + toNum(it.calculated_total_price), 0.00)) ?? 0.00;

        let qtyPO = '';
        try{
            qtyPO = String(getNestVal(base, 'reconciliation.item_quantity', '') ?? '').trim()
        } catch (error) {
            console.error("Error calculating total_price or calculated_total_price:", error);
        }

        if (qtyPO !== '') {
            base.quantity               = 1
            base.invoice_unit_price     = base.total_price
            base.unit_price_net_final   = base.total_price

        } else {
            base.quantity               = round2(groupItems.reduce((acc, it) => acc + toNum(it.quantity), 0));
        }

        import.meta.env.MODE === "development" && console.log("Merging group items:", base);

        const compoundSum = groupItems.reduce((acc, it) => acc + toNum(it.compound_quantity), 0);
        if (compoundSum) base.compound_quantity = round2(compoundSum);

        // description ← linked PO item description
        const poDesc = String(getNestVal(base, 'reconciliation.item_description', '') ?? '').trim();

        if (poDesc) base.description = poDesc;

        result.push({ idx: groupItems[0].__srcIdx, item: base });
    });

    // restore original top-to-bottom ordering by first-seen index
    result.sort((a, b) => a.idx - b.idx);
    const merged = result.map((r, i) => ({ ...r.item, item_number: i + 1 }));

    return { merged, mergedGroups, removed };
}

export const generateInvRowStyles = ( prps: IDetailsRowProps | undefined ) => {
    const mismatched = isTotToTotMssmatched(prps);
    const explained   = mismatched ? explainTotalPriceMismatch(prps?.item) : null;

    // real, unexplained mismatch — total_price and calculated_total_price disagree and no known
    // presentation flag (vat/discount/post_subtract_item_vat) accounts for it. Stays a warning.
    if (mismatched && !explained) return {
        ...( invDetailsRowMainStyles || {} ),
        root: {
            ...( typeof invDetailsRowMainStyles?.root === "object" ? invDetailsRowMainStyles?.root : {} ),
            backgroundColor: "#ffa931",
            border: prps?.item?.is_new ? "1px dashed #9bde7e" : undefined,
            marginLeft: prps?.item?.is_new ? "-1px" : undefined,
            marginRight: prps?.item?.is_new ? "-1px" : undefined,

            selectors: {
                ":hover": {
                    backgroundColor: "#ffbd61"
                }
            }
        },

        cell: {
            ...( typeof invDetailsRowMainStyles?.cell === "object" ? invDetailsRowMainStyles?.cell : {} ),
            alignContent: "space-evenly"
        }
    };

    // mismatched but explained by a known transform — not a warning, just informational.
    // calculated_total_price is never altered; this only changes how the row is painted.
    if (mismatched && explained) return {
        ...( invDetailsRowMainStyles || {} ),
        root: {
            ...( typeof invDetailsRowMainStyles?.root === "object" ? invDetailsRowMainStyles?.root : {} ),
            backgroundColor: "#e8f5e9",
            border: prps?.item?.is_new ? "1px dashed #9bde7e" : "1px dashed #7ec98f",
            marginLeft: prps?.item?.is_new ? "-1px" : undefined,
            marginRight: prps?.item?.is_new ? "-1px" : undefined,
            selectors: {
                ":hover": {
                    backgroundColor: "#d4ecd6"
                }
            }
        }
    };

    return {
        ...( invDetailsRowMainStyles || {} ),
        root: {
            ...( typeof invDetailsRowMainStyles?.root === "object" ? invDetailsRowMainStyles?.root : {} ),
            border: prps?.item?.is_new ? "1px dashed #9bde7e" : undefined,
            marginLeft: prps?.item?.is_new ? "-1px" : undefined,
            marginRight: prps?.item?.is_new ? "-1px" : undefined,
            selectors: {
                ":hover": {
                    backgroundColor: "#f3f3f3"
                }
            }
        }
    };
}

export const sortInvItemTableNested = (invItems: any[], column: IColumn, isDescending: boolean) => {
    return [...invItems].sort((a, b) => {
        const aValue = getNestVal(a, column.fieldName || '', '');
        const bValue = getNestVal(b, column.fieldName || '', '');
        if (aValue < bValue) return isDescending ? 1 : -1;
        if (aValue > bValue) return isDescending ? -1 : 1;
        return 0;
    });
};

// apply column order to columns
export const applyColumnOrder = (columns: IColumn[], columnOrder: string[]): IColumn[] => {
    if (columnOrder.length === 0) return columns;

    const orderedCols: IColumn[] = [];
    const columnMap = new Map(columns.map(col => [col.key, col]));

    // add columns in saved order
    columnOrder.forEach(key => {
        const col = columnMap.get(key);
        if (col) {
            orderedCols.push(col);
            columnMap.delete(key);
        }
    });

    // add any remaining columns that weren't in the saved order
    columnMap.forEach(col => orderedCols.push(col));

    return orderedCols;
};